import React from 'react';
import { Col, Row, Card, Space, Typography } from 'antd';
import { DownloadOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useCustom } from '@refinedev/core';
import { LineChart } from '../charts/LineChart';
import { StatList } from '../stats/StatList';
import { TOKEN_KEY } from '../../authProvider';

interface DownloadDataPoint {
  date: string;
  count: number;
}

interface DownloadLocation {
  country: string;
  count: number;
}

interface DownloadStatsResponse {
  totalDownloads: DownloadDataPoint[];
  userDownloads: DownloadDataPoint[];
  coachDownloads: DownloadDataPoint[];
  organizationAdminDownloads: DownloadDataPoint[];
  uninstalls: DownloadDataPoint[];
  downloadLocations: DownloadLocation[];
  totalDownloadsTotal: number;
  userDownloadsTotal: number;
  coachDownloadsTotal: number;
  organizationAdminDownloadsTotal: number;
  uninstallsTotal: number;
}

interface DownloadsAnalyticsProps {
  dateRange: { from: string; to: string };
}

// Convert ISO country code to full country name
const getCountryName = (code: string): string => {
  if (!code || code === 'Unknown') return 'Unknown';
  
  const trimmedCode = code.trim();
  
  // If it's already a long name (more than 3 chars), return as-is
  if (trimmedCode.length > 3) return trimmedCode;
  
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    const name = regionNames.of(trimmedCode.toUpperCase());
    if (name && name !== trimmedCode.toUpperCase()) {
      return name;
    }
    return trimmedCode;
  } catch (e) {
    // Fallback if code is invalid
    console.warn('Failed to convert country code:', trimmedCode, e);
    return trimmedCode;
  }
};

export const DownloadsAnalytics: React.FC<DownloadsAnalyticsProps> = ({ dateRange }) => {
  const { data, isLoading, isRefetching } = useCustom<DownloadStatsResponse>({
    url: `${import.meta.env.VITE_API_URL}/admin/dashboard/download_stats`,
    method: "get",
    config: {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem(TOKEN_KEY)}`
      },
      query: {
        from: dateRange.from,
        to: dateRange.to,
      }
    },
  });

  const responseData = data?.data;
  const totalDownloads = responseData?.totalDownloads ?? [];
  const userDownloads = responseData?.userDownloads ?? [];
  const coachDownloads = responseData?.coachDownloads ?? [];
  const organizationAdminDownloads = responseData?.organizationAdminDownloads ?? [];
  const downloadLocations = responseData?.downloadLocations ?? [];

  // Calculate totals from daily data
  const totalSum = totalDownloads.reduce((sum, d) => sum + d.count, 0);
  const userSum = userDownloads.reduce((sum, d) => sum + d.count, 0);
  const coachSum = coachDownloads.reduce((sum, d) => sum + d.count, 0);
  const organizationAdminSum = organizationAdminDownloads.reduce((sum, d) => sum + d.count, 0);

  // Calculate total for percentage calculation
  const locationTotal = downloadLocations.reduce((sum, loc) => sum + loc.count, 0);

  return (
    <>
      <Col xs={24} md={14}>
        <Card
          title={
            <Space>
              <DownloadOutlined style={{ fontSize: '24px', color: '#FF3131' }} />
              <Typography.Title level={5} style={{ margin: 0 }}>
                User Registrations
              </Typography.Title>
            </Space>
          }
        >
          <LineChart
            title=""
            icon={null}
            data={[
              ...totalDownloads.map(d => ({ ...d, type: 'Total' })),
              ...userDownloads.map(d => ({ ...d, type: 'User' })),
              ...coachDownloads.map(d => ({ ...d, type: 'Coach' })),
              ...organizationAdminDownloads.map(d => ({ ...d, type: 'Organization admin' })),
            ]}
            metrics={[
              {
                label: 'Total Signups',
                value: totalSum,
                color: 'hsl(347 77% 50%)',  // Deep ruby
                type: 'Total'
              },
              {
                label: 'Users',
                value: userSum,
                color: 'hsl(347 77% 60%)',  // Medium ruby
                type: 'User'
              },
              {
                label: 'Coaches',
                value: coachSum,
                color: 'hsl(347 77% 70%)',  // Light ruby
                type: 'Coach'
              },
              {
                label: 'Organization admins',
                value: organizationAdminSum,
                color: 'hsl(25 85% 68%)',
                type: 'Organization admin'
              },
            ]}
            xField="date"
            yField="count"
            height={250}
            isLoading={isLoading || isRefetching}
            bare
          />
        </Card>
      </Col>

      <Col xs={24} md={10}>
        <StatList
          title="Registrations by country"
          icon={<EnvironmentOutlined style={{ fontSize: '24px', color: '#FF3131' }} />}
          items={downloadLocations.map(loc => {
            const rawPercentage = locationTotal > 0 ? (loc.count / locationTotal) * 100 : 0;
            // Round to one decimal place (e.g., 0.1%)
            const percentage = Math.round(rawPercentage * 10) / 10;
            return {
              label: getCountryName(loc.country),
              value: loc.count,
              percentage,
            };
          })}
          formatValue={(value) => `${value.toLocaleString()} accounts`}
          height={430}
          pageSize={5}
          style={{
            padding: 2,
            gap: 10,
          }}
          labelStyle={{
            fontSize: 'clamp(12px, 1.5vw, 14px)',
            fontWeight: 500,
          }}
          valueStyle={{
            fontSize: 'clamp(13px, 1.6vw, 15px)',
            fontWeight: 600,
            color: '#FF3131',
          }}
          percentageStyle={{
            fontSize: 'clamp(10px, 1.2vw, 12px)',
            fontWeight: 500,
          }}
          paginationStyle={{
            padding: 'clamp(6px, 1.2vw, 10px)',
            gap: 'clamp(2px, 0.8vw, 6px)',
            fontSize: 'clamp(12px, 1.5vw, 14px)',
          }}
        />
      </Col>
    </>
  );
};
